import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { films } from "@/mocks/films";
import { emptyFilters, filterFilms, searchFilms, sortFilms } from "@/utils/filmQuery";
import type { FilterState, SortKey } from "@/utils/filmQuery";
import FilmCard from "@/components/feature/FilmCard";
import Breadcrumb from "@/components/base/Breadcrumb";

const hotKeywords = ["色即是空", "河智苑", "王家卫", "金基德", "日常", "青春"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("query") ?? "";
  const [draft, setDraft] = useState(query);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("rating");

  useEffect(() => {
    setDraft(query);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `搜索「${query}」 - 色即是空影视资料检索`
      : "影视检索 - 色即是空影视资料站";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        query
          ? `在色即是空影视资料站检索「${query}」相关的影视作品、演员与主创资料，快速定位剧情介绍与相关推荐。`
          : "按片名、原名、导演、演员姓名或类型标签检索经典影视作品，支持年份、地区与评分二次筛选。",
      );
  }, [query]);

  const baseResults = useMemo(() => (query ? searchFilms(films, query) : films), [query]);
  const results = useMemo(
    () => sortFilms(filterFilms(baseResults, filters), sort),
    [baseResults, filters, sort],
  );

  const submit = (value: string) => {
    const keyword = value.trim();
    if (keyword) setSearchParams({ query: keyword });
    else setSearchParams({});
  };

  const recommended = sortFilms(films, "popular").slice(0, 4);
  const hasQuery = query.trim().length > 0;

  return (
    <div className="w-full bg-background-50">
      <div className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 pb-8 pt-24 md:px-8 md:pb-10 md:pt-32">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "资料检索" },
              ...(hasQuery ? [{ label: query }] : []),
            ]}
          />
          <h1 className="mt-4 font-heading text-[26px] font-bold leading-tight text-foreground-950 md:text-[34px]">
            {hasQuery ? `「${query}」的检索结果` : "影视资料检索"}
          </h1>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit(draft);
            }}
            className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-50 px-2 py-1.5"
          >
            <span className="flex h-9 w-9 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="输入片名、演员或导演，例如：色即是空"
              aria-label="搜索关键词"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="shrink-0 cursor-pointer rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">热门检索</span>
            {hotKeywords.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => submit(keyword)}
                className="cursor-pointer rounded-full bg-background-200/80 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                {keyword}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="flex flex-col gap-3 border-b border-background-200/80 pb-4 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-foreground-600">
            {hasQuery ? (
              <>
                关键词 <span className="font-semibold text-foreground-950">{query}</span> 共找到{" "}
                <span className="font-semibold text-primary-700">{results.length}</span> 部相关作品
              </>
            ) : (
              <>
                未输入关键词，当前展示全部 <span className="font-semibold text-primary-700">{results.length}</span> 部作品
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <select
                value={filters.year}
                onChange={(event) => setFilters({ ...filters, year: event.target.value })}
                aria-label="按年份筛选"
                className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 outline-none"
              >
                <option value="全部">全部年份</option>
                <option value="2020后">2020 后</option>
                <option value="2010-2019">2010-2019</option>
                <option value="2000-2009">2000-2009</option>
                <option value="2000前">2000 前</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
                <i className="ri-arrow-down-s-line" />
              </span>
            </div>
            <div className="relative">
              <select
                value={filters.region}
                onChange={(event) => setFilters({ ...filters, region: event.target.value })}
                aria-label="按地区筛选"
                className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 outline-none"
              >
                <option value="全部">全部地区</option>
                <option value="韩国">韩国</option>
                <option value="中国">中国</option>
                <option value="日本">日本</option>
                <option value="中国香港">中国香港</option>
                <option value="美国">美国</option>
                <option value="法国">法国</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
                <i className="ri-arrow-down-s-line" />
              </span>
            </div>
            <div className="relative">
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value as SortKey)}
                aria-label="排序方式"
                className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 outline-none"
              >
                <option value="rating">评分最高</option>
                <option value="newest">最新上线</option>
                <option value="popular">热度最高</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-500">
                <i className="ri-arrow-down-s-line" />
              </span>
            </div>
          </div>
        </div>

        {results.length === 0 ? (
          <div className="mt-10">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
              <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-xl" />
              </span>
              <h2 className="font-heading text-lg font-semibold text-foreground-900">
                未找到相关影视作品
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                试着输入更短的片名，或直接搜索演员姓名，也可以浏览下方热门作品。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilters(emptyFilters);
                    setDraft("");
                    setSearchParams({});
                  }}
                  className="cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  查看全部作品
                </button>
                <Link
                  to="/category"
                  className="rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700"
                >
                  进入分类页
                </Link>
              </div>
            </div>
            <h2 className="mb-4 mt-10 font-heading text-lg font-semibold text-foreground-950">
              热门影视推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {recommended.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {results.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}